import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, PackageCheck, Trash2 } from 'lucide-react';
import { Button, buttonClass, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';

/** Popover ichidagi bitta yozuv — savatning ko'rinish shakli. */
export interface ReadyEntry {
  /** Savatdagi O'RNI: bitta satr ikki katakka bo'linsa ikkita yozuv bo'ladi. */
  index: number;
  label: string;
  qty: number;
  unit: string;
  cellCode: string;
}

export interface ReadyPopoverProps {
  entries: readonly ReadyEntry[];
  onRemove: (index: number) => void;
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Tayyor pozitsiyalar ro'yxati.
 *
 * Ikki joyda ko'rinadi: toolbar dagi popover ichida va joylashtirish
 * sahifasida forma ostida. Ro'yxat BITTA marta yozilgan — ikkinchi
 * nusxa bo'lsa, ulardan biriga qo'shilgan maydon ikkinchisiga yetib
 * bormasdi.
 */
export const ReadyList = ({ entries, onRemove }: ReadyPopoverProps) => (
  <ul className="flex flex-col gap-1">
    {entries.map((entry) => (
      <li
        key={`${entry.index}-${entry.label}`}
        className={cn(
          'flex items-center gap-2 rounded-[var(--radius-sm)]',
          'px-2 py-1.5 hover:bg-surface-2',
        )}
      >
        <span className="min-w-0 flex-1 truncate text-sm text-text" title={entry.label}>
          {entry.label}
        </span>

        {entry.cellCode === '' ? null : (
          <span className="tabular shrink-0 text-xs text-text-faint">{entry.cellCode}</span>
        )}

        <span className="shrink-0 text-sm font-medium text-text tabular-nums">
          {entry.qty}
          {entry.unit === '' ? '' : ` ${entry.unit}`}
        </span>

        <Button
          variant="ghost"
          size="sm"
          aria-label={`${t('supply.removeFromBasket')} — ${entry.label}`}
          title={t('supply.removeFromBasket')}
          onClick={() => onRemove(entry.index)}
          className="shrink-0 px-2"
        >
          <Trash2 size={14} aria-hidden />
        </Button>
      </li>
    ))}
  </ul>
);

/**
 * «TAYYOR: N» — yuborishga tayyor pozitsiyalar.
 *
 * Ilgari bu o'ngdagi to'liq balandlikdagi ustun edi. U ekranning uchdan
 * birini egallardi, ichida ikki-uchta satr turardi va ostida ulkan bo'sh
 * joy qolardi; savat bo'shaganda esa ustun butunlay yo'qolib, jadval
 * kengligi sakrardi.
 *
 * Endi u toolbar ichidagi ochiladigan ro'yxat: joyni faqat KERAK bo'lganda
 * egallaydi, jadval esa doim to'liq kenglikda qoladi va hech qachon qayta
 * o'lchanmaydi.
 *
 * Ro'yxat bo'sh bo'lsa trigger O'CHIQ: bo'sh popover ochilishi «bosdim —
 * hech narsa bo'lmadi» degan holat yaratardi.
 */
export const ReadyPopover = ({ entries, onRemove }: ReadyPopoverProps) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const empty = entries.length === 0;

  /* Oxirgi yozuv o'chirilganda popover O'ZI yopiladi — aks holda bo'sh
     quti ochiq qolib, triggeri esa o'chib turardi. */
  useEffect(() => {
    if (empty) setOpen(false);
  }, [empty]);

  /*
   * KLAVIATURA VA SICHQONCHA.
   *
   * Uchta xatti-harakat bitta effektda, chunki uchalasi ham ayni bir
   * shartga bog'liq (`open`) va bitta tozalashni talab qiladi:
   *
   *   Esc     — yopadi va fokusni triggerga QAYTARADI. Qaytarilmasa,
   *             klaviatura bilan yurgan odam sahifaning boshiga tushib
   *             qolardi.
   *   Tab     — fokus popover ICHIDA aylanadi. Ochiq popover ostidagi
   *             jadvalga o'tib ketgan fokus «men qayerdaman?» degan
   *             holat yaratadi.
   *   Tashqi  — `pointerdown` ataylab: `click` tugmaning qo'yib
   *   bosish    yuborilishini kutadi va shu orada ostidagi element
   *             bosilib ulgurardi.
   */
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      const panel = panelRef.current;
      if (panel === null) return;

      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }

      if (event.key !== 'Tab') return;

      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = items[0];
      const last = items[items.length - 1];

      /* Ichida bosiladigan narsa qolmasa — fokus qutining o'zida
         turadi va tashqariga chiqib ketmaydi. */
      if (first === undefined || last === undefined) {
        event.preventDefault();
        panel.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    };

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node | null;
      if (target === null) return;
      if (panelRef.current?.contains(target) === true) return;
      if (triggerRef.current?.contains(target) === true) return;
      setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open]);

  /* Ochilganda fokus ICHKARIGA. Birinchi bosiladigan element bo'lmasa
     qutining o'zi oladi (`tabIndex={-1}`). */
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (panel === null) return;
    (panel.querySelector<HTMLElement>(FOCUSABLE) ?? panel).focus();
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        disabled={empty}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((current) => !current)}
        className={cn(buttonClass({ variant: 'secondary', size: 'sm' }), 'gap-1.5')}
      >
        <PackageCheck size={14} aria-hidden />
        {t('supply.ready', { count: entries.length })}
        <ChevronDown
          size={14}
          aria-hidden
          className={cn(
            'transition-transform duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
            open ? 'rotate-180' : undefined,
          )}
        />
      </button>

      {open ? (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          /* Modal EMAS: ostidagi jadval o'qilishi kerak va sahifa
             qoraytirilmaydi. Fokus esa baribir ichida ushlanadi —
             yuqoridagi `Tab` ishlovchisiga qarang. */
          aria-modal="false"
          aria-label={t('supply.readyTitle')}
          tabIndex={-1}
          className={cn(
            'motion-pop-in absolute top-[calc(100%+0.5rem)] left-0 z-[var(--z-popover)]',
            'w-[22rem] max-w-[calc(100vw-2rem)] rounded-[var(--radius-md)]',
            'border border-border bg-surface p-2 shadow-[var(--shadow-2)]',
          )}
        >
          <p className="px-2 py-1.5 text-xs font-semibold tracking-wide text-text-muted uppercase">
            {t('supply.readyTitle')}
          </p>

          {/* 280px dan oshsa ICHIDA aylanadi — popover ekrandan chiqib
              ketmasin. */}
          <div className="max-h-[280px] overflow-y-auto">
            <ReadyList entries={entries} onRemove={onRemove} />
          </div>
        </div>
      ) : null}
    </div>
  );
};
